/*
 * OrderForm.tsx : Un formulaire pour ajouter ou modifier une commande.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { faBox } from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	useFormDirty,
} from '../../../common';
import CardHeader from 'layout/CardHeader';

// Import Tiers pour le select
import { tiersApi } from 'features/tiers';

// Import Commandes
import { OrderData, typeOptions } from '../types/types';
import { ordersApi } from '../services/ordersApi';

// Composant OrderForm
const OrderForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Formulaire
	const [formData, setFormData] = React.useState<OrderData>({
		id: 0,
		referenceNumber: '',
		orderVersions: [],
		type: 'UNIQUE',
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hook des Options des Tiers
	const [TiersOptions, setTiersOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingTiers, setLoadingTiers] = React.useState(true);

	// Hook pour le prochain numéro de référence (uniquement CCC/AA ou A = année et C = compteur)
	const [nextRef, setNextRef] = React.useState<string | null>(null);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si un id est présent, on fetch les données
		if (id) {
			ordersApi
				.get(Number(id), token)
				.then((data: OrderData) => {
					setFormData(data);
					// Préremplir les selects
					if (data.tiers) {
						setTiersOptions([
							{
								key: data.tiers.id.toString(),
								label: data.tiers.label,
							},
						]);
					}
				})
				.catch((error) => {
					console.error('Erreur lors du chargement de la commande :', error);
					setLoading(false);
				})
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Sinon, pas d'id, on arrête le loading
			setLoading(false);
		}
	}, [id]);

	// Lazy load des options de tiers
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingTiers) {
			tiersApi
				.listSelectSuppliers(token)
				.then((items) => {
					const options = items.map((item) => ({
						key: item.id.toString(),
						label: String(item.label),
					}));
					setTiersOptions(options);
				})
				.catch((error) => console.error(error))
				.finally(() => setLoadingTiers(false));
		}
	}, [loading, loadingTiers]);

	// Lazy load du prochain numéro de référence
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande
		if (id) return;

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			ordersApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					// Mise à jour du numéro de référence dans le formulaire
					setFormData((prevData) => ({
						...prevData,
						referenceNumber: data.nextReference,
					}));
					setBaselineReady(true);
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const navigateAfterSuccess = () => {
		// On recherche la dernière version de la commande correspondant et on navigue vers sa page de gestion
		const latestVersion = formData.orderVersions
			?.filter((v) => v.status !== 'CANCELED')
			?.reduce((prev, current) =>
				prev.versionIndex > current.versionIndex ? prev : current
			);
		if (latestVersion) {
			navigate(`/app/orderVersions/${latestVersion.id}/manage`);
		} else if (id) {
			console.warn("Aucune version de commande trouvée pour l'élément", id);
		} else {
			navigate('/app/orders');
		}
	};

	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Si un id est présent, mise à jour de la commande
		if (id) {
			ordersApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: navigateAfterSuccess,
						markClean,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la mise à jour de la commande :', error)
				)
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'une nouvelle commande, et on supprime le numéro de référence car il doit être recalculé côté backend pour éviter les conflits
			const keysToRemove = ['referenceNumber'] as const;

			const dataToCreate = Object.fromEntries(
				Object.entries(formData).filter(
					([key]) =>
						!keysToRemove.includes(key as (typeof keysToRemove)[number])
				)
			);
			ordersApi
				.create(dataToCreate, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate('/app/orders'),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/orders/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la création de la commande :', error)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est tiers, on doit mettre à jour l'objet complet
		if (name === 'tiers') {
			const selectedOption = TiersOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData({
					...formData,
					tiers: {
						id: selectedOption.key as unknown as number,
						label: selectedOption.label,
					},
				});
			}
			return;
		}

		// si name est type, on met à jour le type
		if (name === 'type') {
			setFormData({ ...formData, type: value });
			return;
		}

		// Pour les autres champs, mise à jour standard
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Mettre la ref quand le type change
	React.useEffect(() => {
		if (!formData.referenceNumber) return;

		// Enlever l'ancien suffixe
		const baseRef = formData.referenceNumber.replace(/-A$|-R$/i, '');

		// Ajouter le suffixe selon le type
		let suffix = '';
		if (formData.type === 'ANNUAL') suffix = '-A';
		else if (formData.type === 'REGULATION') suffix = '-R';

		setFormData((prev) => ({
			...prev,
			referenceNumber: baseRef + suffix,
		}));
	}, [formData.type]);

	// Rendu du composant principal
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faBox}
					title={id ? "Édition d'une commande" : 'Nouvelle commande'}
					createdAt={
						formData.createdAt
							? formData.createdAt?.toLocaleString()
							: undefined
					}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<SelectField
									label="Classification de la commande"
									selectedKey={formData.type || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'type',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={typeOptions}
									loading={loading}
									optionsLoading={false}
								/>
								<InputField
									label="Numéro de référence"
									name="referenceNumber"
									value={formData.referenceNumber}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="Calculé automatiquement la première fois"
									readOnly={id ? false : true}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<SelectField
									label="Tiers"
									selectedKey={formData.tiers?.id.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'tiers',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={TiersOptions}
									loading={loading}
									optionsLoading={loadingTiers}
									entityHref={
										formData.tiers?.id
											? `/app/tiers/${formData.tiers.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											tiers: null,
										}))
									}
								/>
							</div>
						</div>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							if (id) {
								navigateAfterSuccess();
							} else {
								navigate('/app/orders');
							}
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default OrderForm;
